import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, qs } from '../api/client';
import { Bandeau, Carte, Squelette } from '../components/ui';
import { ajouteJours, dateFr, isoJour, JOURS, montant } from '../lib/format';
import type { Vehicule } from '../api/types';

interface Occ {
  raison: string; reservationId?: string; statut?: string;
  debut: string; fin: string; libereLe: string; reference?: string; detail: string;
}
interface Ligne {
  vehicule: { id: string; marque: string; modele: string; annee: number; immatriculation: string; categorie: string; statut: string };
  occupations: Occ[];
}
interface Reponse { debut: string; fin: string; jours: number; tamponPreparationMin: number; lignes: Ligne[] }

interface Trou {
  vehicleId: string; libelle: string; immatriculation: string;
  debut: string; fin: string; jours: number;
  valeurEstimeeMinor: number; avant: string; apres: string;
}
interface Trous { nombre: number; joursTotal: number; valeurTotaleMinor: number; trous: Trou[] }

interface Dispo {
  disponibles: { vehicle: Vehicule; tarifJourMinor: number }[];
  occupes: { vehicle: Vehicule; libereLe: string; detail: string }[];
  message: string;
}

const MS_J = 86400000;
const CLE_FILTRE = 'friends.planning.vehicules';
const CLE_COULEURS = 'friends.planning.couleurs';

/**
 * Les couches du planning.
 *
 * Trois seulement par defaut — louee, reservee, disponible : c'est la lecture
 * que le patron veut, et toute couleur en plus la brouille. Les trois autres
 * (retard, entretien, trou a vendre) s'allument a la demande, avec leur
 * couleur au choix.
 *
 * Une couche eteinte ne fait PAS disparaitre l'occupation : une voiture au
 * garage reste occupee. Elle reprend simplement une des trois couleurs de base
 * — sinon le planning mentirait, et c'est le genre de mensonge qui fait sortir
 * une voiture qui n'aurait pas du.
 */
interface Couche {
  cle: 'loue' | 'reserve' | 'dispo' | 'retard' | 'entretien' | 'trou';
  label: string;
  defaut: string;
  /** Les trois de base ne s'eteignent pas : elles SONT le planning. */
  base?: boolean;
  aide?: string;
}
const COUCHES: Couche[] = [
  { cle: 'loue', label: 'Louée', defaut: '#15803D', base: true },
  { cle: 'reserve', label: 'Réservée', defaut: '#6D28D9', base: true },
  { cle: 'dispo', label: 'Disponible', defaut: '#FCEAEA', base: true },
  { cle: 'retard', label: 'En retard', defaut: '#C62828', aide: 'sinon compté comme louée' },
  { cle: 'entretien', label: 'Entretien', defaut: '#8A929E', aide: 'sinon compté comme réservée' },
  { cle: 'trou', label: 'Trou à vendre', defaut: '#F2A33C', aide: 'les jours creux entre deux locations' },
];

type Reglage = Record<string, { actif: boolean; couleur: string }>;
const REGLAGE_DEFAUT: Reglage = Object.fromEntries(
  COUCHES.map((c) => [c.cle, { actif: Boolean(c.base), couleur: c.defaut }]),
);

/** Le 1er du mois de la date donnee, a minuit. */
const premierDuMois = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
/** Nombre de jours entre le 1er du mois de `d` et la fin du n-ieme mois suivant. */
const joursSurMois = (d: Date, nbMois: number) =>
  Math.round((new Date(d.getFullYear(), d.getMonth() + nbMois, 1).getTime() - premierDuMois(d).getTime()) / MS_J);

export default function Planning() {
  /*
   * Le patron raisonne en mois, du 1er au 31 — c'est comme ca qu'il lit ses
   * chiffres et son planning. La periode part donc toujours du 1er, et se
   * compte en mois entiers, pas en « 30 jours glissants ».
   */
  const [ancre, setAncre] = useState(() => premierDuMois(new Date()));
  const [nbMois, setNbMois] = useState(1);
  const [d, setD] = useState<Reponse | null>(null);
  const [trous, setTrous] = useState<Trous | null>(null);
  const [survol, setSurvol] = useState<{ o: Occ; v: string } | null>(null);

  /* --- Choix des voitures affichees, conserve d'une visite a l'autre --- */
  const [tousVehicules, setTousVehicules] = useState<Vehicule[]>([]);
  const [choisis, setChoisis] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(CLE_FILTRE) ?? '[]') as string[]; } catch { return []; }
  });
  const [filtreOuvert, setFiltreOuvert] = useState(false);

  /* --- Couleurs, choisies une fois et conservees --- */
  const [couleurs, setCouleurs] = useState<Reglage>(() => {
    try {
      const brut = JSON.parse(localStorage.getItem(CLE_COULEURS) ?? 'null') as Reglage | null;
      return brut ? { ...REGLAGE_DEFAUT, ...brut } : REGLAGE_DEFAUT;
    } catch { return REGLAGE_DEFAUT; }
  });
  const [couleursOuvert, setCouleursOuvert] = useState(false);
  useEffect(() => { localStorage.setItem(CLE_COULEURS, JSON.stringify(couleurs)); }, [couleurs]);

  /* --- Selection au glisser : deux dates, et la reponse --- */
  const piste = useRef<HTMLDivElement | null>(null);
  const [glisse, setGlisse] = useState<{ a: number; b: number } | null>(null);
  const [selection, setSelection] = useState<{ debut: Date; fin: Date } | null>(null);
  const [dispo, setDispo] = useState<Dispo | null>(null);

  const jours = joursSurMois(ancre, nbMois);

  useEffect(() => {
    localStorage.setItem(CLE_FILTRE, JSON.stringify(choisis));
  }, [choisis]);

  useEffect(() => { api.get<Vehicule[]>('/vehicules').then(setTousVehicules).catch(() => {}); }, []);

  useEffect(() => {
    setD(null);
    api.get<Reponse>(`/disponibilites/planning${qs({
      debut: isoJour(ancre), jours, vehicules: choisis.length ? choisis.join(',') : undefined,
    })}`).then(setD).catch(() => {});
    api.get<Trous>(`/disponibilites/trous${qs({ debut: isoJour(ancre), jours })}`)
      .then(setTrous).catch(() => setTrous(null));
  }, [ancre, jours, choisis]);

  const colonnes = useMemo(
    () => Array.from({ length: jours }, (_, i) => ajouteJours(ancre, i)),
    [ancre, jours],
  );
  const auj = new Date().setHours(0, 0, 0, 0);

  /* --- Glisser sur une piste : on lit la fraction horizontale --- */
  const dateDeFraction = useCallback((f: number) => {
    const i = Math.max(0, Math.min(jours - 1, Math.floor(f * jours)));
    return ajouteJours(ancre, i);
  }, [ancre, jours]);

  const fractionDeSouris = (e: { clientX: number }, el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    return Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
  };

  /**
   * Les ecouteurs sont poses DANS le mousedown, pas dans un effet.
   *
   * Passer par un useEffect fait perdre les premiers mouvements : le temps que
   * React re-rende et attache l'ecouteur, la souris a deja bouge. Un humain ne
   * le voit pas — le rendu prend 16 ms — mais un glisser rapide, ou un doigt sur
   * un pave tactile, perd le debut du geste et la selection se reduit a un jour.
   */
  function demarrerGlisse(e: React.MouseEvent<HTMLDivElement>) {
    // Un clic sur une barre ouvre le dossier : on ne demarre une selection que
    // sur le fond de la piste.
    if ((e.target as HTMLElement).closest('.pl-barre')) return;
    const el = e.currentTarget;
    piste.current = el;
    const depart = fractionDeSouris(e, el);
    let arrivee = depart;
    setGlisse({ a: depart, b: depart });
    setSelection(null); setDispo(null);

    const bouge = (ev: MouseEvent) => {
      arrivee = fractionDeSouris(ev, el);
      setGlisse({ a: depart, b: arrivee });
    };
    const relache = () => {
      window.removeEventListener('mousemove', bouge);
      window.removeEventListener('mouseup', relache);
      setGlisse(null);
      const [f1, f2] = depart <= arrivee ? [depart, arrivee] : [arrivee, depart];
      // La fin est INCLUSIVE : glisser sur une seule case veut dire « ce jour-la ».
      setSelection({ debut: dateDeFraction(f1), fin: ajouteJours(dateDeFraction(f2), 1) });
    };
    window.addEventListener('mousemove', bouge);
    window.addEventListener('mouseup', relache);
  }

  useEffect(() => {
    if (!selection) { setDispo(null); return; }
    setDispo(null);
    api.get<Dispo>(`/disponibilites${qs({ debut: isoJour(selection.debut), fin: isoJour(selection.fin) })}`)
      .then(setDispo).catch(() => {});
  }, [selection]);

  if (!d) return <Squelette lignes={8} />;

  const pos = (debutIso: string, finIso: string) => {
    const t0 = ancre.getTime();
    const total = jours * MS_J;
    const a = Math.max(t0, new Date(debutIso).getTime());
    const b = Math.min(t0 + total, new Date(finIso).getTime());
    if (b <= a) return null;
    return { left: `${((a - t0) / total) * 100}%`, width: `${Math.max(0.6, ((b - a) / total) * 100)}%` };
  };

  /*
   * Trois couleurs, et rien d'autre — c'est la demande du patron, et c'est la
   * bonne : vert la voiture travaille, violet elle est promise, rouge elle ne
   * rapporte rien. L'entretien n'est pas un de ces trois etats : il reste en
   * hachures grises pour ne pas fausser la lecture.
   */
  const couleur = (o: Occ): string => {
    // Un entretien occupe la voiture : si la couche est eteinte, il prend la
    // couleur « reservee » plutot que de disparaitre.
    if (o.raison !== 'reservation') return couleurs.entretien?.actif ? 'entretien' : 'reserve';
    const enRetard = new Date(o.fin) < new Date() && o.statut !== 'terminee';
    const loue = o.statut === 'en_cours' || o.statut === 'non_restitue';
    if (enRetard) return couleurs.retard?.actif ? 'retard' : (loue ? 'loue' : 'reserve');
    return loue ? 'loue' : 'reserve';
  };

  const trousParVehicule = new Map<string, Trou[]>();
  for (const t of trous?.trous ?? []) {
    if (!trousParVehicule.has(t.vehicleId)) trousParVehicule.set(t.vehicleId, []);
    trousParVehicule.get(t.vehicleId)!.push(t);
  }

  // Au-dela de deux mois, un numero par jour devient illisible : on bascule sur
  // des reperes de mois.
  const denses = jours > 62;
  const moisAffiches = Array.from({ length: nbMois }, (_, i) => {
    const m = new Date(ancre.getFullYear(), ancre.getMonth() + i, 1);
    const debut = Math.round((m.getTime() - ancre.getTime()) / MS_J);
    const fin = Math.round((new Date(m.getFullYear(), m.getMonth() + 1, 1).getTime() - ancre.getTime()) / MS_J);
    return { m, debut, largeur: fin - debut };
  });

  const titreMois = ancre.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const dernierMois = new Date(ancre.getFullYear(), ancre.getMonth() + nbMois - 1, 1)
    .toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>Planning</h1>
          <p>
            {d.lignes.length} véhicule(s) ·{' '}
            {nbMois === 1 ? titreMois : `de ${titreMois} à ${dernierMois}`}
            {choisis.length > 0 && ` · ${choisis.length} voiture(s) sélectionnée(s)`}
          </p>
        </div>
        <div className="actions">
          <button className="btn btn-s" onClick={() => setAncre(new Date(ancre.getFullYear(), ancre.getMonth() - nbMois, 1))}>‹</button>
          <button className="btn btn-s" onClick={() => setAncre(premierDuMois(new Date()))}>Ce mois-ci</button>
          <button className="btn btn-s" onClick={() => setAncre(new Date(ancre.getFullYear(), ancre.getMonth() + nbMois, 1))}>›</button>
          <select className="btn btn-s" value={nbMois} onChange={(e) => setNbMois(Number(e.target.value))} style={{ paddingRight: 8 }}>
            <option value={1}>1 mois</option><option value={2}>2 mois</option>
            <option value={3}>3 mois</option><option value={6}>6 mois</option>
            <option value={12}>12 mois</option>
          </select>
          <button className={`btn btn-s ${choisis.length ? 'btn-p' : ''}`} onClick={() => setFiltreOuvert(!filtreOuvert)}>
            {choisis.length ? `${choisis.length} voiture(s)` : 'Choisir les voitures'}
          </button>
          <button className="btn btn-s" onClick={() => setCouleursOuvert(!couleursOuvert)}>Couleurs</button>
        </div>
      </div>

      {filtreOuvert && (
        <Carte titre="Voitures affichées" action={
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-s" onClick={() => setChoisis([])}>Toutes</button>
            <button className="btn btn-s" onClick={() => setFiltreOuvert(false)}>Fermer</button>
          </div>
        }>
          <p className="mini sourdine" style={{ marginTop: -4, marginBottom: 10 }}>
            Aucune coche = toutes les voitures. Votre choix est conservé pour la prochaine fois.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 8 }}>
            {tousVehicules.map((v) => (
              <label key={v._id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={choisis.includes(v._id)}
                       onChange={(e) => setChoisis(e.target.checked
                         ? [...choisis, v._id]
                         : choisis.filter((x) => x !== v._id))} />
                <span>{v.marque} {v.modele} <span className="mini sourdine">{v.immatriculation}</span></span>
              </label>
            ))}
          </div>
        </Carte>
      )}

      {couleursOuvert && (
        <Carte titre="Couleurs du planning" action={
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-s" onClick={() => setCouleurs(REGLAGE_DEFAUT)}>Rétablir</button>
            <button className="btn btn-s" onClick={() => setCouleursOuvert(false)}>Fermer</button>
          </div>
        }>
          <p className="mini sourdine" style={{ marginTop: -4, marginBottom: 12 }}>
            Trois couleurs par défaut — louée, réservée, disponible. Les autres s'allument si vous
            le voulez. Une couche éteinte ne cache jamais une voiture occupée : elle reprend
            simplement une des trois couleurs de base.
          </p>
          <div className="couleurs">
            {COUCHES.map((c) => (
              <label key={c.cle} className="couleur-ligne">
                <input type="color" value={couleurs[c.cle]?.couleur ?? c.defaut}
                       aria-label={`Couleur — ${c.label}`}
                       onChange={(e) => setCouleurs({
                         ...couleurs,
                         [c.cle]: { actif: couleurs[c.cle]?.actif ?? Boolean(c.base), couleur: e.target.value },
                       })} />
                <span className="nom">
                  {c.label}
                  {c.aide && <div className="fixe">{c.aide}</div>}
                </span>
                {c.base ? (
                  <span className="fixe">toujours</span>
                ) : (
                  <input type="checkbox" checked={couleurs[c.cle]?.actif ?? false}
                         aria-label={`Afficher ${c.label}`}
                         onChange={(e) => setCouleurs({
                           ...couleurs,
                           [c.cle]: { actif: e.target.checked, couleur: couleurs[c.cle]?.couleur ?? c.defaut },
                         })} />
                )}
              </label>
            ))}
          </div>
        </Carte>
      )}

      {/*
        * « L'argent qui dort » : une voiture qui rentre mardi et repart samedi a
        * trois jours creux au milieu. Ces journees-la sont les plus faciles a
        * vendre — la voiture est deja la, deja preparee — et personne ne les
        * voyait, parce qu'elles ne sont ni une location ni une voiture libre.
        */}
      {couleurs.trou?.actif && trous && trous.nombre > 0 && (
        <div style={{ margin: '10px 0 14px' }}>
          <Bandeau type="warn" titre={`${trous.nombre} trou(s) dans le planning — ${montant(trous.valeurTotaleMinor)} d'argent qui dort`}>
            {trous.joursTotal} journée(s) où une voiture est immobile <b>entre deux locations</b>.
            Elles sont hachurées en orange ci-dessous : la voiture est déjà là, déjà préparée.
          </Bandeau>
        </div>
      )}

      <div className="legende" style={{ marginBottom: 10 }}>
        {COUCHES.filter((c) => couleurs[c.cle]?.actif).map((c) => (
          <span key={c.cle}>
            <i style={{ background: couleurs[c.cle]!.couleur, border: '1px solid rgba(0,0,0,.12)' }} />
            {c.label}
          </span>
        ))}
      </div>

      <div className="planning" style={Object.fromEntries(
        COUCHES.map((c) => [`--pl-${c.cle}`, couleurs[c.cle]?.couleur ?? c.defaut]),
      ) as React.CSSProperties}>
        <div className="pl-grille">
          <div className="pl-tete">
            <div className="pl-veh"><b>Véhicule</b></div>
            {denses ? (
              <div className="pl-jours" style={{ gridTemplateColumns: `repeat(${jours},1fr)` }}>
                {moisAffiches.map((x, i) => (
                  <div key={i} className="pl-jour pl-mois"
                       style={{ gridColumn: `${x.debut + 1} / span ${x.largeur}` }}>
                    <b>{x.m.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' })}</b>
                  </div>
                ))}
              </div>
            ) : (
              <div className="pl-jours" style={{ gridTemplateColumns: `repeat(${jours},1fr)` }}>
                {colonnes.map((c, i) => {
                  const j = new Date(c);
                  const we = j.getDay() === 0 || j.getDay() === 6;
                  const estAuj = new Date(c).setHours(0, 0, 0, 0) === auj;
                  return (
                    <div key={i} className={`pl-jour ${we ? 'we' : ''} ${estAuj ? 'auj' : ''}`}>
                      {JOURS[j.getDay()]}<b>{j.getDate()}</b>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {d.lignes.map((l) => (
            <div className="pl-ligne" key={l.vehicule.id}>
              <div className="pl-veh">
                <b>{l.vehicule.marque} {l.vehicule.modele}</b>
                <span>{l.vehicule.immatriculation} · {l.vehicule.categorie}</span>
              </div>
              <div className="pl-piste" onMouseDown={demarrerGlisse}>
                <div className="pl-fond" style={{ gridTemplateColumns: `repeat(${jours},1fr)` }}>
                  {colonnes.map((c, i) => {
                    const j = new Date(c);
                    const we = j.getDay() === 0 || j.getDay() === 6;
                    const estAuj = new Date(c).setHours(0, 0, 0, 0) === auj;
                    return <div key={i} className={`pl-cell ${we ? 'we' : ''} ${estAuj ? 'auj' : ''}`} />;
                  })}
                </div>

                {(couleurs.trou?.actif ? trousParVehicule.get(l.vehicule.id) ?? [] : []).map((t, i) => {
                  const p = pos(t.debut, t.fin);
                  return p && (
                    <div key={`t${i}`} className="pl-trou" style={p}
                         title={`${t.jours} jour(s) libres entre deux locations — ${montant(t.valeurEstimeeMinor)} à vendre`} />
                  );
                })}

                {l.occupations.map((o, i) => {
                  const p = pos(o.debut, o.fin);
                  const tampon = pos(o.fin, o.libereLe);
                  const classe = couleur(o);
                  return (
                    <span key={i}>
                      {p && (o.reservationId ? (
                        <Link className={`pl-barre ${classe}`} style={p} to={`/reservations/${o.reservationId}`}
                              title={`Ouvrir le dossier ${o.reference ?? ''}`}
                              onMouseEnter={() => setSurvol({ o, v: `${l.vehicule.marque} ${l.vehicule.modele}` })}
                              onMouseLeave={() => setSurvol(null)}>
                          {denses ? '' : o.detail}
                        </Link>
                      ) : (
                        <div className={`pl-barre ${classe}`} style={p}
                             onMouseEnter={() => setSurvol({ o, v: `${l.vehicule.marque} ${l.vehicule.modele}` })}
                             onMouseLeave={() => setSurvol(null)}>
                          {denses ? '' : o.detail}
                        </div>
                      ))}
                      {tampon && o.raison === 'reservation' && (
                        <div className="pl-barre tampon" style={tampon}
                             title={`Préparation — ${d.tamponPreparationMin} min avant la prochaine sortie`} />
                      )}
                    </span>
                  );
                })}

                {glisse && (
                  <div className="pl-selection" style={{
                    left: `${Math.min(glisse.a, glisse.b) * 100}%`,
                    width: `${Math.abs(glisse.b - glisse.a) * 100}%`,
                  }} />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Glisser sur le planning repond a la seule question du comptoir :
          « qu'est-ce qui est libre sur ces dates ? » */}
      {selection && (
        <div style={{ marginTop: 14 }}>
          <Carte titre={`Du ${dateFr(selection.debut)} au ${dateFr(ajouteJours(selection.fin, -1))}`}
                 action={<button className="btn btn-s" onClick={() => { setSelection(null); setDispo(null); }}>Effacer</button>}>
            {dispo === null ? <Squelette lignes={2} /> : (
              <>
                <p style={{ marginTop: -4 }}>{dispo.message}</p>
                {dispo.disponibles.length > 0 && (
                  <>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                      {dispo.disponibles.map((x) => (
                        <Link key={x.vehicle._id} className="btn btn-s"
                              to={`/reservations/nouvelle?vehicle=${x.vehicle._id}&debut=${isoJour(selection.debut)}&fin=${isoJour(selection.fin)}`}>
                          {x.vehicle.marque} {x.vehicle.modele} · {montant(x.tarifJourMinor)}/j
                        </Link>
                      ))}
                    </div>
                    <p className="mini sourdine" style={{ marginTop: 10 }}>
                      Cliquez une voiture pour ouvrir une nouvelle location déjà remplie avec ces dates.
                    </p>
                  </>
                )}
              </>
            )}
          </Carte>
        </div>
      )}

      {survol && (
        <div style={{ position: 'fixed', bottom: 20, left: 280, zIndex: 20 }}>
          <Carte>
            <b>{survol.v}</b>
            <div className="mini sourdine">{survol.o.detail}</div>
            <div className="mini">{dateFr(survol.o.debut)} → {dateFr(survol.o.fin)} · libre le {dateFr(survol.o.libereLe)}</div>
          </Carte>
        </div>
      )}

      <p className="mini sourdine" style={{ marginTop: 12 }}>
        <b>Glissez sur le planning</b> pour voir ce qui est libre sur une période.
        Le rouge n'est pas une erreur : c'est une journée où la voiture ne rapporte rien.
        Une voiture qui rentre à 11 h n'est pas libre à 11 h — la fin hachurée de chaque barre
        est le temps de préparation, et il n'est pas réservable.
      </p>
    </>
  );
}
